
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { ArrowLeft } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HormonalFlowchart } from "./HormonalFlowchart";

export function ClimactericCalculator({ onBack, onResultsUpdate }) {
  const { toast } = useToast();
  const [examValues, setExamValues] = useState({});
  const [clinicalData, setClinicalData] = useState({
    age: "",
    menstrualCycle: "",
    hasUterus: true,
    usingHormones: false,
    selectedSymptoms: {}
  });
  const [showResults, setShowResults] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);

  const symptoms = {
    vasomotor: [
      "Ondas de Calor",
      "Sudorese Noturna",
      "Palpitações"
    ],
    psychological: [
      "Insônia",
      "Irritabilidade",
      "Ansiedade",
      "Depressão"
    ],
    genitourinary: [
      "Secura Vaginal",
      "Dispareunia",
      "Urgência Urinária"
    ],
    physical: [
      "Dores Articulares",
      "Fadiga",
      "Ganho de Peso"
    ]
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setExamValues(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  };

  const handleClinicalDataChange = (field, value) => {
    setClinicalData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSymptomToggle = (symptom) => {
    setClinicalData(prev => ({
      ...prev,
      selectedSymptoms: {
        ...prev.selectedSymptoms,
        [symptom]: !prev.selectedSymptoms[symptom]
      }
    }));
  };

  const determinePhase = (values, clinical) => {
    const { fsh, amh, estradiol } = values;
    const { age, menstrualCycle } = clinical;

    // Menopausa Precoce (< 40 anos)
    if (age < 40 && fsh > 40 && amh < 0.1) {
      return {
        phase: "Menopausa Precoce",
        severity: "high",
        description: "Padrão hormonal sugestivo de menopausa precoce.",
        ovarian_reserve: "Falência ovariana prematura",
        recommendations: [
          "Avaliação endocrinológica urgente",
          "Investigação de causas secundárias",
          "Considerar TRH",
          "Avaliação de densidade óssea",
          "Acompanhamento cardiovascular"
        ]
      };
    }

    // Menopausa Tardia (> 55 anos com ciclos)
    if (age > 55 && menstrualCycle !== "absent") {
      return {
        phase: "Menopausa Tardia",
        severity: "moderate",
        description: "Padrão hormonal sugestivo de menopausa tardia.",
        ovarian_reserve: "Função ovariana prolongada",
        recommendations: [
          "Avaliação ginecológica detalhada",
          "Investigação endometrial",
          "Monitoramento hormonal regular"
        ]
      };
    }

    // Pós-Menopausa
    if (menstrualCycle === "absent" && fsh > 40 && amh < 0.1) {
      return {
        phase: "Pós-Menopausa",
        severity: "moderate",
        description: "Menopausa estabelecida com perfil hormonal característico.",
        ovarian_reserve: "Falência ovariana estabelecida",
        recommendations: [
          "Avaliação de risco cardiovascular",
          "Densitometria óssea",
          "Considerar TRH se indicado",
          "Monitoramento de saúde óssea"
        ]
      };
    }

    // Perimenopausa Inicial
    if (menstrualCycle === "irregular" && fsh >= 10 && fsh <= 25) {
      return {
        phase: "Perimenopausa Inicial",
        severity: "low",
        description: "Fase inicial da transição menopausal.",
        ovarian_reserve: "Declínio inicial da reserva ovariana",
        recommendations: [
          "Monitoramento de sintomas",
          "Considerar contracepção",
          "Avaliação regular"
        ]
      };
    }

    // Perimenopausa Tardia
    if (menstrualCycle === "irregular" && fsh > 25 && fsh <= 40) {
      return {
        phase: "Perimenopausa Tardia",
        severity: "moderate",
        description: "Fase avançada da transição menopausal.",
        ovarian_reserve: "Declínio acentuado da reserva ovariana",
        recommendations: [
          "Considerar TRH para sintomas",
          "Avaliação de riscos",
          "Monitoramento mais frequente"
        ]
      };
    }

    // Pré-Menopausa
    return {
      phase: "Pré-Menopausa",
      severity: "low",
      description: "Função ovariana preservada.",
      ovarian_reserve: "Reserva ovariana adequada para idade",
      recommendations: [
        "Acompanhamento preventivo",
        "Exames de rotina",
        "Orientações sobre transição futura"
      ]
    };
  };

  const handleExamSubmit = (e) => {
    e.preventDefault();
    
    // Análise dos resultados
    const results = determinePhase(examValues, clinicalData);
    
    setAnalysisResults(results);
    setShowResults(true);
    
    if (onResultsUpdate) onResultsUpdate(results);

    toast({
      title: "Análise Concluída",
      description: "Seus resultados estão prontos",
    });
  };

  return (
    <motion.div
      className="mt-20 max-w-4xl mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex items-center gap-4 mb-6">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-[#ff3366] hover:text-[#ff3366]/80 hover:bg-[#1a1a2e]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <h2 className="text-xl font-bold text-[#ff3366]">
          Calculadora de Climatério
        </h2>
      </div>

      <form onSubmit={handleExamSubmit} className="space-y-6">
        {/* Dados Clínicos */}
        <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
          <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
            Dados Clínicos
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <input
              type="number"
              placeholder="Idade"
              value={clinicalData.age}
              onChange={(e) => handleClinicalDataChange("age", e.target.value)}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              required
            />
            <Select
              value={clinicalData.menstrualCycle}
              onValueChange={(value) => handleClinicalDataChange("menstrualCycle", value)}
            >
              <SelectTrigger className="bg-[#16213e] border-[#ff3366]/20">
                <SelectValue placeholder="Ciclo Menstrual" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="regular">Regular</SelectItem>
                <SelectItem value="irregular">Irregular</SelectItem>
                <SelectItem value="absent">Ausente > 12 meses</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="hasUterus"
                checked={clinicalData.hasUterus}
                onCheckedChange={(checked) => handleClinicalDataChange("hasUterus", checked)}
              />
              <label htmlFor="hasUterus" className="text-white">
                Presença de Útero
              </label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="usingHormones"
                checked={clinicalData.usingHormones}
                onCheckedChange={(checked) => handleClinicalDataChange("usingHormones", checked)}
              />
              <label htmlFor="usingHormones" className="text-white">
                Uso de Hormônios/TRH
              </label>
            </div>
          </div>
        </div>

        {/* Sintomas */}
        <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
          <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
            Sintomas
          </h3>
          <div className="grid gap-6 md:grid-cols-2">
            {Object.entries(symptoms).map(([category, symptomList]) => (
              <div key={category} className="space-y-2">
                <h4 className="font-medium text-white">
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </h4>
                {symptomList.map((symptom) => (
                  <div key={symptom} className="flex items-center gap-2">
                    <Checkbox
                      id={symptom}
                      checked={clinicalData.selectedSymptoms[symptom] || false}
                      onCheckedChange={() => handleSymptomToggle(symptom)}
                    />
                    <label htmlFor={symptom} className="text-white/80">
                      {symptom}
                    </label>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Exames */}
        <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
          <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
            Exames Laboratoriais
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            <input
              type="number"
              name="fsh"
              placeholder="FSH (mUI/mL)"
              onChange={handleInputChange}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              step="0.01"
              required
            />
            <input
              type="number"
              name="lh"
              placeholder="LH (mUI/mL)"
              onChange={handleInputChange}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              step="0.01"
              required
            />
            <input
              type="number"
              name="amh"
              placeholder="AMH (ng/mL)"
              onChange={handleInputChange}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              step="0.01"
              required
            />
            <input
              type="number"
              name="estradiol"
              placeholder="Estradiol (pg/mL)"
              onChange={handleInputChange}
              className="p-2 rounded bg-[#16213e] border border-[#ff3366]/20 text-white"
              step="0.01"
              required
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full bg-[#ff3366] hover:bg-[#ff3366]/80 text-white"
        >
          Analisar Resultados
        </Button>
      </form>

      {showResults && analysisResults && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 space-y-6"
        >
          {/* Status do Climatério */}
          <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
            <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
              Status do Climatério
            </h3>
            <div className={`p-3 rounded-lg ${
              analysisResults.severity === "high"
                ? "bg-red-500/20 border-red-500/40"
                : analysisResults.severity === "moderate"
                  ? "bg-yellow-500/20 border-yellow-500/40"
                  : "bg-green-500/20 border-green-500/40"
            }`}>
              <h4 className="font-semibold text-white text-lg">
                {analysisResults.phase}
              </h4>
              <p className="text-white/80 mt-2">
                {analysisResults.description}
              </p>
            </div>
          </div>

          {/* Reserva Ovariana */}
          <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
            <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
              Reserva Ovariana
            </h3>
            <div className="space-y-4">
              <div className="p-3 bg-[#1a1a2e]/50 rounded border border-[#ff3366]/20">
                <h4 className="font-semibold text-white">
                  Status: {analysisResults.ovarian_reserve}
                </h4>
                <p className="text-white/80 mt-2">
                  AMH: {examValues.amh} ng/mL
                  {examValues.amh > 1.0 ? " (Adequada)" :
                   examValues.amh > 0.5 ? " (Reduzida)" : " (Muito reduzida)"}
                </p>
              </div>
            </div>
          </div>

          {/* Recomendações */}
          <div className="p-4 rounded-lg bg-[#16213e]/50 border border-[#ff3366]/20">
            <h3 className="text-lg font-semibold text-[#ff3366] mb-4">
              Recomendações
            </h3>
            <ul className="space-y-2">
              {analysisResults.recommendations.map((rec, index) => (
                <li
                  key={index}
                  className="text-white/80 flex items-start gap-2"
                >
                  <span className="text-[#ff3366]">•</span>
                  {rec}
                </li>
              ))}
            </ul>
          </div>

          {/* Disclaimer */}
          <div className="p-4 rounded-lg bg-blue-500/20 border border-blue-500/40">
            <p className="text-sm text-blue-400">
              ⚠️ Esta análise é baseada em diretrizes médicas atuais e serve apenas como referência.
              Consulte sempre um profissional de saúde para avaliação e tratamento adequados.
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
